// Course flowsheets: a semester-by-semester grid of a program's courses whose tiles light up
// the prerequisite chain behind a course and the courses it unlocks (modeled on engineering
// "flowsheet" advisement pages). The program sheet is
// derived from the captured requirement tree, catalog prerequisite text, and season offerings.
// Everything here is a pure projection; it never evaluates degree completion.

import type { Requirement } from '@/app/types/interfaces';
import { equivalentCourseCodes } from '@/app/lib/courseEquivalencies';

export type FlowsheetTileKind = 'course' | 'choice' | 'slot' | 'filler';
export type FlowsheetSeason = 'Fall' | 'Spring';

export interface FlowsheetTile {
  id: string;
  kind: FlowsheetTileKind;
  /** Normalized 8-digit codes. One for a course, the alternatives for a choice, empty otherwise. */
  codes: string[];
  title: string;
  credits: number;
  term: number;
  row: number;
  /** Requirement the tile stands for (choice, slot) or belongs to (course). */
  requirement?: string;
  /** Up to a handful of eligible courses for a slot, for the tile's description. */
  examples?: string[];
}

export interface FlowsheetTerm {
  index: number;
  season: FlowsheetSeason;
  /** 1-based academic year of the sheet. */
  year: number;
  credits: number;
}

/** Prerequisite relation between two tiles: `from` must come before `to`. */
export interface FlowsheetEdge { from: string; to: string }

export interface Flowsheet {
  degreeId: number;
  degreeName: string;
  scope: 'major' | 'program';
  terms: FlowsheetTerm[];
  tiles: FlowsheetTile[];
  edges: FlowsheetEdge[];
  notes: string[];
  totalCredits: number;
}

export interface FlowsheetCourseFacts {
  title: string;
  credits: number | null;
  /** Prerequisites in the DNF shape produced by `parsePrerequisiteGroups`. */
  prereqGroups: string[][][];
  /** Regular seasons the catalog offers the course in; null when unknown. */
  seasons: FlowsheetSeason[] | null;
}

export interface BuildProgramFlowsheetInput {
  degree: { ID: number; Name: string; requirements?: Requirement[] | null };
  lists: Map<string, { classes: string[]; name?: string }>;
  course: (code: string) => FlowsheetCourseFacts | null;
}

const MAX_TERMS = 12;
const DEFAULT_CREDITS = 3;

export const flowsheetCode = (code: string): string => {
  const digits = String(code || '').replace(/\D/g, '');
  return digits.length === 6 ? `01${digits}` : digits;
};
const isCourseCode = (entry: string) => /^\d{8}$/.test(flowsheetCode(entry)) && !/[a-z_]/i.test(entry.replace(/[:\s]/g, ''));
const equivalents = (code: string) => [code, ...equivalentCourseCodes(code)];
const courseLevel = (code: string) => Number(code.slice(5, 6)) || 1;

const CAPSTONE = /capstone|senior (design|project|thesis|seminar)/i;

/** Earliest term a course of this level normally appears in a four-year sheet. */
function levelFloor(code: string, label = ''): number {
  if (CAPSTONE.test(label)) return 6;
  const level = courseLevel(code);
  return level <= 1 ? 0 : level === 2 ? 1 : level === 3 ? 2 : level === 4 ? 4 : 6;
}

/** A broader requirement accepts a narrower slot only when all of its courses qualify; elective
 * lists routinely include a few cross-listed courses from other departments. */
const POOL_CONTAINMENT = 1;

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

type Demand =
  | { kind: 'course'; code: string; requirement: string }
  | { kind: 'choice'; codes: string[]; requirement: string }
  | { kind: 'slot'; title: string; pool: Set<string>; need: number; listBacked: boolean };

/** Walks the requirement tree into concrete demands. An OR of sub-requirements follows one branch. */
function collectDemands(requirements: Requirement[], input: BuildProgramFlowsheetInput, notes: string[]): Demand[] {
  const demands: Demand[] = [];
  const known = (code: string) => input.course(code) !== null;
  const visit = (node: Requirement) => {
    if (/residen|in residence/i.test(`${node.title ?? ''} ${node.description ?? ''}`)) {
      notes.push(`${node.title ?? 'Residency requirement'}: verify separately; residency cannot be established from a suggested sequence.`);
      return;
    }
    const children = node.requirements ?? [];
    if (children.length && node.logic === 'or') {
      const chosen = children.find((child) => {
        const codes = (child.course_pool ?? []).filter(isCourseCode).map(flowsheetCode);
        return codes.length > 0 && codes.every(known);
      }) ?? children[0];
      if (children.length > 1) notes.push(`${node.title ?? 'Requirement'}: showing ${chosen.title ?? 'the first option'}. ${children.length - 1} other option${children.length === 2 ? '' : 's'} also count.`);
      visit(chosen);
    } else {
      children.forEach(visit);
    }
    const pool = node.course_pool ?? [];
    if (!pool.length) return;
    const title = node.title ?? 'Requirement';
    const explicit = Array.from(new Set(pool.filter(isCourseCode).map(flowsheetCode)));
    const listNames = pool.filter((entry) => !isCourseCode(entry));
    const expanded = new Set(explicit);
    listNames.forEach((name) => {
      const list = input.lists.get(name);
      if (!list?.classes.length) notes.push(`${title}: eligible courses for ${name} are unavailable; the open slots need review.`);
      (list?.classes ?? []).forEach((code) => expanded.add(flowsheetCode(code)));
    });
    const poolCredits = median(Array.from(expanded).map((code) => input.course(code)?.credits ?? 0).filter((value) => value > 0)) ?? DEFAULT_CREDITS;
    const need = node.courses_needed
      ?? (node.credits_needed ? Math.max(1, Math.ceil(node.credits_needed / Math.max(1, poolCredits))) : node.logic === 'and' ? pool.length : 1);
    if (!listNames.length && need >= explicit.length) {
      explicit.forEach((code) => demands.push({ kind: 'course', code, requirement: title }));
    } else if (!listNames.length && need === 1 && explicit.length <= 4) {
      demands.push({ kind: 'choice', codes: explicit, requirement: title });
    } else {
      demands.push({ kind: 'slot', title, pool: expanded, need, listBacked: listNames.length > 0 });
    }
  };
  requirements.forEach(visit);
  return demands;
}

/** Picks the prerequisite alternative best covered by tiles on the sheet and returns its clauses. */
function sheetClauses(groups: string[][][], onSheet: (code: string) => boolean): string[][] {
  let best: string[][] = [];
  let bestScore = -1;
  groups.forEach((group) => {
    const clauses = group.filter((clause) => !clause.some((option) => option.startsWith('>='))).map((clause) => clause.map(flowsheetCode));
    const score = clauses.filter((clause) => clause.some(onSheet)).length;
    if (score > bestScore) { best = clauses; bestScore = score; }
  });
  return best;
}

/** Prerequisite edges between tiles, deduplicated and acyclic. */
export function flowsheetEdges(tiles: Array<Pick<FlowsheetTile, 'id' | 'codes'>>, prereqGroups: (code: string) => string[][][]): FlowsheetEdge[] {
  const tileByCode = new Map<string, string>();
  tiles.forEach((tile) => tile.codes.forEach((code) => equivalents(code).forEach((alias) => { if (!tileByCode.has(alias)) tileByCode.set(alias, tile.id); })));
  const edges = new Map<string, FlowsheetEdge>();
  tiles.forEach((tile) => {
    tile.codes.forEach((code) => {
      sheetClauses(prereqGroups(code), (option) => tileByCode.has(option)).forEach((clause) => {
        const from = clause.map((option) => tileByCode.get(option)).find((id) => id && id !== tile.id);
        if (from) edges.set(`${from}>${tile.id}`, { from, to: tile.id });
      });
    });
  });
  // Catalog text occasionally lists courses as each other's prerequisites; keep the first direction.
  const kept: FlowsheetEdge[] = [];
  const reaches = (start: string, target: string) => {
    const stack = [start];
    const seen = new Set<string>();
    while (stack.length) {
      const id = stack.pop() as string;
      if (id === target) return true;
      if (seen.has(id)) continue;
      seen.add(id);
      kept.forEach((edge) => { if (edge.from === id) stack.push(edge.to); });
    }
    return false;
  };
  edges.forEach((edge) => { if (!reaches(edge.to, edge.from)) kept.push(edge); });
  return kept;
}

/**
 * Lines prerequisite chains up on one row where possible: a tile takes the row of its first
 * predecessor when that cell is free, everything else fills the first open rows in order.
 */
export function assignFlowsheetRows<T extends Pick<FlowsheetTile, 'id' | 'term' | 'kind'>>(tiles: T[], edges: FlowsheetEdge[]): Map<string, number> {
  const rows = new Map<string, number>();
  const kindRank: Record<FlowsheetTileKind, number> = { course: 0, choice: 1, slot: 2, filler: 3 };
  const terms = Array.from(new Set(tiles.map((tile) => tile.term))).sort((a, b) => a - b);
  terms.forEach((term) => {
    const column = tiles.filter((tile) => tile.term === term).sort((a, b) => kindRank[a.kind] - kindRank[b.kind]);
    const taken = new Set<number>();
    const pending: T[] = [];
    column.forEach((tile) => {
      const preferred = edges.filter((edge) => edge.to === tile.id).map((edge) => rows.get(edge.from)).filter((row): row is number => row !== undefined).sort((a, b) => a - b)
        .find((row) => !taken.has(row));
      if (preferred !== undefined && tile.kind !== 'filler') { rows.set(tile.id, preferred); taken.add(preferred); } else pending.push(tile);
    });
    // Tiles that continue an earlier chain keep their row; the rest pack from the top.
    pending.forEach((tile) => {
      let row = 0;
      while (taken.has(row)) row++;
      rows.set(tile.id, row);
      taken.add(row);
    });
  });
  return rows;
}

const seasonAt = (term: number): FlowsheetSeason => (term % 2 === 0 ? 'Fall' : 'Spring');

export function buildProgramFlowsheet(input: BuildProgramFlowsheetInput): Flowsheet {
  const { degree } = input;
  const scope: Flowsheet['scope'] = /^major\b|\bbachelor\b|\bb\.?s\.?\b|\bb\.?a\.?\b/i.test(degree.Name) && !/minor|certificate/i.test(degree.Name) ? 'major' : 'program';
  const notes: string[] = ['Illustrative sequence from captured program requirements and catalog prerequisites; confirm your catalog year, alternatives, residency, grades and school-wide requirements with an advisor. Course and slot credits may be estimated. Historical offerings do not guarantee future availability.'];
  const demands = collectDemands(degree.requirements ?? [], input, notes);
  const facts = (code: string) => input.course(code);
  const creditsOf = (code: string) => facts(code)?.credits || DEFAULT_CREDITS;

  type Draft = Omit<FlowsheetTile, 'term' | 'row'> & { pool?: Set<string>; floor: number; seasons: FlowsheetSeason[] | null };
  const drafts: Draft[] = [];
  const onSheet = new Set<string>();
  const add = (draft: Draft) => { drafts.push(draft); draft.codes.forEach((code) => equivalents(code).forEach((alias) => onSheet.add(alias))); };

  demands.forEach((demand) => {
    if (demand.kind === 'course' && !onSheet.has(demand.code)) {
      const course = facts(demand.code);
      add({ id: `c-${demand.code}`, kind: 'course', codes: [demand.code], title: course?.title || demand.code, credits: creditsOf(demand.code), requirement: demand.requirement, floor: levelFloor(demand.code, `${course?.title ?? ''} ${demand.requirement}`), seasons: course?.seasons ?? null });
    }
  });
  demands.forEach((demand, index) => {
    if (demand.kind !== 'choice' || demand.codes.some((code) => onSheet.has(code))) return;
    const seasons = new Set<FlowsheetSeason>();
    let anyUnknown = false;
    demand.codes.forEach((code) => { const s = facts(code)?.seasons; if (s) s.forEach((season) => seasons.add(season)); else anyUnknown = true; });
    add({ id: `x-${index}`, kind: 'choice', codes: demand.codes, title: demand.requirement, credits: creditsOf(demand.codes[0]), requirement: demand.requirement, floor: Math.min(...demand.codes.map((code) => levelFloor(code, demand.requirement))), seasons: anyUnknown || !seasons.size ? null : Array.from(seasons) });
  });

  // Elective requirements overlap ("7 electives, 5 from CS, 2 upper level", "7 courses in
  // residence"). Narrow pools go first; a broader requirement counts courses and slots already
  // on the sheet that it would accept before adding slots of its own.
  const slotDemands = demands.filter((demand): demand is Extract<Demand, { kind: 'slot' }> => demand.kind === 'slot').sort((a, b) => a.pool.size - b.pool.size);
  slotDemands.forEach((demand, demandIndex) => {
    if (/residen|in residence/i.test(demand.title)) {
      notes.push(`${demand.title}: verify separately; residency cannot be established from a suggested sequence.`);
      return;
    }
    const accepted = drafts.filter((draft) => draft.kind === 'slot'
      ? draft.pool !== undefined && draft.pool.size > 0 && Array.from(draft.pool).filter((code) => demand.pool.has(code)).length >= draft.pool.size * POOL_CONTAINMENT
      : draft.codes.some((code) => demand.pool.has(code))).length;
    const remaining = Math.max(0, demand.need - accepted);
    if (!remaining) {
      notes.push(`${demand.title} overlaps courses or slots already shown; confirm the final choices satisfy each constraint.`);
      return;
    }
    const pool = Array.from(demand.pool).filter((code) => !onSheet.has(code));
    const credits = median(pool.map((code) => facts(code)?.credits ?? 0).filter((value) => value > 0)) ?? DEFAULT_CREDITS;
    const levels = pool.map(courseLevel);
    const upper = /upper|advanced|senior|capstone|\b[34]\d\d\b/i.test(demand.title) || (levels.length > 0 && levels.every((level) => level >= 3));
    const floor = CAPSTONE.test(demand.title) ? 6 : scope === 'major' ? (upper ? 4 : 2) : (upper ? 2 : 0);
    for (let i = 0; i < remaining; i++) {
      add({ id: `s-${demandIndex}-${i}`, kind: 'slot', codes: [], title: demand.title, credits, requirement: demand.title, pool: demand.pool, examples: pool.slice(0, 6), floor, seasons: null });
    }
  });

  const majorCap = scope === 'major' ? 12 : 7;
  drafts.forEach(draft => draft.codes.forEach(code => {
    const missing = sheetClauses(facts(code)?.prereqGroups ?? [], option => onSheet.has(option)).filter(clause => !clause.some(option => onSheet.has(option)));
    if (missing.length) notes.push(`${draft.title}: additional prerequisite options outside this sheet (${missing.map(clause => clause.join(' or ')).join('; ')}) need review.`);
  }));
  const edges = flowsheetEdges(drafts, (code) => facts(code)?.prereqGroups ?? []);
  const predecessors = new Map<string, string[]>();
  edges.forEach((edge) => predecessors.set(edge.to, [...(predecessors.get(edge.to) ?? []), edge.from]));

  // Topological order (edges are acyclic), shallow and low-level courses first.
  const depth = new Map<string, number>();
  const depthOf = (id: string): number => {
    if (depth.has(id)) return depth.get(id) as number;
    depth.set(id, 0);
    const value = Math.max(0, ...(predecessors.get(id) ?? []).map((from) => depthOf(from) + 1));
    depth.set(id, value);
    return value;
  };
  const order = [...drafts].sort((a, b) => (a.kind === 'slot' ? 1 : 0) - (b.kind === 'slot' ? 1 : 0) || depthOf(a.id) - depthOf(b.id) || a.floor - b.floor || a.id.localeCompare(b.id));

  const load: number[] = [];
  const termOf = new Map<string, number>();
  const fits = (term: number, credits: number) => (load[term] ?? 0) + credits <= majorCap || (load[term] ?? 0) === 0;
  order.forEach((draft) => {
    const after = Math.max(-1, ...(predecessors.get(draft.id) ?? []).map((from) => termOf.get(from) ?? -1));
    const start = Math.max(after + 1, draft.floor);
    let chosen = -1;
    if (draft.kind === 'slot') {
      // Electives spread across the lightest eligible terms of the sheet.
      const horizon = Math.max(scope === 'major' ? 8 : 0, load.length, start + 1);
      for (let term = start; term < Math.min(MAX_TERMS, horizon); term++) {
        if (fits(term, draft.credits) && (chosen < 0 || (load[term] ?? 0) < (load[chosen] ?? 0))) chosen = term;
      }
    }
    for (let term = start; chosen < 0 && term < MAX_TERMS; term++) {
      if (draft.seasons && !draft.seasons.includes(seasonAt(term))) continue;
      if (fits(term, draft.credits)) chosen = term;
    }
    if (chosen < 0) {
      chosen = start;
      if (draft.seasons && !draft.seasons.includes(seasonAt(chosen))) chosen++;
      notes.push(`${draft.title}: the suggested sequence exceeds the usual load or duration; review placement.`);
    }
    termOf.set(draft.id, chosen);
    load[chosen] = (load[chosen] ?? 0) + draft.credits;
  });

  const placed: Array<Omit<FlowsheetTile, 'row'>> = drafts.map(({ pool: _pool, floor: _floor, seasons: _seasons, ...tile }) => ({ ...tile, term: termOf.get(tile.id) ?? 0 }));
  const termCount = Math.max(scope === 'major' ? 8 : 1, ...placed.map((tile) => tile.term + 1));

  const rows = assignFlowsheetRows(placed, edges);
  const tiles = placed.map((tile) => ({ ...tile, row: rows.get(tile.id) ?? 0 }));
  const terms = Array.from({ length: termCount }, (_, index): FlowsheetTerm => ({
    index, season: seasonAt(index), year: Math.floor(index / 2) + 1,
    credits: tiles.filter((tile) => tile.term === index).reduce((sum, tile) => sum + tile.credits, 0),
  }));
  return { degreeId: degree.ID, degreeName: degree.Name, scope, terms, tiles, edges, notes, totalCredits: terms.reduce((sum, term) => sum + term.credits, 0) };
}

export type FlowsheetRole = 'focus' | 'prereq' | 'prereq-chain' | 'unlocks' | 'unlocks-chain';

/** Roles that hovering or focusing one tile gives the rest of the sheet. */
export function flowsheetRoles(edges: FlowsheetEdge[], focusId: string | null): Map<string, FlowsheetRole> {
  const roles = new Map<string, FlowsheetRole>();
  if (!focusId) return roles;
  roles.set(focusId, 'focus');
  const walk = (direction: 'up' | 'down') => {
    const direct = direction === 'up' ? 'prereq' : 'unlocks';
    const chain = direction === 'up' ? 'prereq-chain' : 'unlocks-chain';
    let frontier = [focusId];
    let first = true;
    while (frontier.length) {
      const next: string[] = [];
      frontier.forEach((id) => edges.forEach((edge) => {
        const other = direction === 'up' ? (edge.to === id ? edge.from : null) : (edge.from === id ? edge.to : null);
        if (other && !roles.has(other)) { roles.set(other, first ? direct : chain); next.push(other); }
      }));
      frontier = next;
      first = false;
    }
  };
  walk('up');
  walk('down');
  return roles;
}
